import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Package, ShoppingBag, Tag, ChatCircleDots, Wallet } from '@phosphor-icons/react';
import { API, photoUrl, formatPrice } from '../utils/marketplace';

const OrderRow = ({ order, role }) => (
  <div className="bg-white dark:bg-neutral-800 border-2 border-black rounded-xl p-3 flex gap-3 items-center" data-testid={`order-row-${order._id}`}>
    <Link to={`/marketplace/listing/${order.listing_id}`} className="w-14 h-14 shrink-0 border-2 border-black rounded-lg overflow-hidden bg-neutral-100 dark:bg-neutral-900 flex items-center justify-center">
      {order.listing_photo
        ? <img src={photoUrl(order.listing_photo)} alt="" className="w-full h-full object-cover" />
        : <Package size={22} weight="duotone" className="text-neutral-300" />}
    </Link>
    <div className="min-w-0 flex-1">
      <p className="font-bold text-sm text-neutral-900 dark:text-white truncate">{order.listing_title}</p>
      <p className="text-xs text-neutral-500">
        {new Date(order.created_at).toLocaleDateString()} · <span className="uppercase font-black text-emerald-600">{order.status}</span>
      </p>
      {role === 'sale' ? (
        <p className="text-sm font-black text-emerald-600">
          +{formatPrice(order.seller_earnings)} <span className="text-[10px] font-bold text-neutral-400">({formatPrice(order.amount)} − {order.commission_pct}% fee)</span>
        </p>
      ) : (
        <p className="text-sm font-black text-[#D95800]">{formatPrice(order.amount)}</p>
      )}
    </div>
    <Link
      to={`/messages/${role === 'sale' ? order.buyer_id : order.seller_id}`}
      className="p-2 bg-[#2B4C6F] text-white border-2 border-black rounded-lg shrink-0"
      title={role === 'sale' ? 'Message buyer' : 'Message seller'}
      data-testid={`order-message-${order._id}`}
    >
      <ChatCircleDots size={16} weight="fill" />
    </Link>
  </div>
);

export default function MarketplaceOrders() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [tab, setTab] = useState('purchases');
  const [data, setData] = useState({ purchases: [], sales: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) { navigate('/login?redirect=/marketplace/orders'); return; }
    axios.get(`${API}/api/marketplace/orders`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token, navigate]);

  const list = data[tab] || [];

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 pb-16" data-testid="marketplace-orders-page">
      <div className="max-w-3xl mx-auto px-4 py-6">
        <h1 className="text-2xl md:text-3xl font-black uppercase text-neutral-900 dark:text-white">Marketplace Orders</h1>
        <div className="flex gap-2 mt-4">
          <button
            onClick={() => setTab('purchases')}
            className={`inline-flex items-center gap-1.5 text-xs font-black uppercase px-4 py-2 border-2 border-black rounded-full ${tab === 'purchases' ? 'bg-[#D95800] text-white' : 'bg-white dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400'}`}
            data-testid="orders-tab-purchases"
          >
            <ShoppingBag size={14} weight="bold" /> Purchases ({data.purchases.length})
          </button>
          <button
            onClick={() => setTab('sales')}
            className={`inline-flex items-center gap-1.5 text-xs font-black uppercase px-4 py-2 border-2 border-black rounded-full ${tab === 'sales' ? 'bg-[#2B4C6F] text-white' : 'bg-white dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400'}`}
            data-testid="orders-tab-sales"
          >
            <Tag size={14} weight="bold" /> Sales ({data.sales.length})
          </button>
        </div>

        {tab === 'sales' && data.sales.length > 0 && (
          <div className="mt-3 bg-emerald-50 dark:bg-emerald-900/20 border-2 border-emerald-600 rounded-xl p-3 flex items-center gap-2 text-sm text-emerald-800 dark:text-emerald-300">
            <Wallet size={18} weight="fill" className="shrink-0" />
            Sale earnings are credited to your ConstructHelp wallet — withdraw anytime from your dashboard.
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20"><div className="animate-spin w-10 h-10 border-4 border-[#D95800] border-t-transparent rounded-full" /></div>
        ) : list.length === 0 ? (
          <div className="text-center py-20" data-testid="orders-empty">
            <Package size={56} weight="duotone" className="text-neutral-300 dark:text-neutral-600 mx-auto mb-4" />
            <p className="font-black uppercase text-neutral-700 dark:text-neutral-300">No {tab} yet</p>
            <Link to="/marketplace" className="text-sm font-bold text-[#D95800] underline underline-offset-4 mt-2 inline-block">Browse the marketplace</Link>
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            {list.map((o) => <OrderRow key={o._id} order={o} role={tab === 'sales' ? 'sale' : 'purchase'} />)}
          </div>
        )}
      </div>
    </div>
  );
}
